import { Asset, TenantRole } from '@/api/generatedApi';
import { AiBadge } from '@/components/AiBadge';
import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetFileName, getAssetFolderDirectory } from '@/utils/assets';
import { getAssetStateDisplayText } from '@/utils/assetStateDisplayText';
import { Box, Link, TableBody, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AssetTable, AssetTableCellLabel, AssetTableCellValue, AssetTableRow } from './AssetTable';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { Info } from '@mui/icons-material';
import { useGetAssetTenantDisplayName } from '@/hooks/useGetAssetTenantDisplayName';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { AssetLabelRow } from '../AssetLabels/AssetLabels';

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const AssetDetails = ({ asset }: { asset?: Asset }) => {
  const { getPath } = useTenants();
  const { permissions } = useGetCurrentUser();
  const { tenant } = useTenants();
  const { isSemanticDescriptionVisibilityEnabled } = useFeatureFlags();
  const { getAssetTenantDisplayName } = useGetAssetTenantDisplayName();
  const assetTenantDisplayName = asset ? getAssetTenantDisplayName(asset) : '';

  const showTenantInfo = asset?.tenantId !== tenant?.tenantId || tenant?.tenantRole === TenantRole.Search;

  if (!asset) {
    return;
  }

  return (
    <StyledBox>
      <AssetTable size='small'>
        <TableBody>
          {showTenantInfo && (
            <AssetTableRow>
              <Tooltip title={`This asset is owned by ${assetTenantDisplayName}`}>
                <AssetTableCellLabel>
                  <Box display='flex' alignItems='center'>
                    <BodyText>Source</BodyText>
                    <Info sx={{ fontSize: '16px', marginLeft: '4px' }} />
                  </Box>
                </AssetTableCellLabel>
              </Tooltip>
              <AssetTableCellValue>
                <Ellipsis text={assetTenantDisplayName} />
              </AssetTableCellValue>
            </AssetTableRow>
          )}
          <AssetTableRow>
            <AssetTableCellLabel>
              <Ellipsis text='Name' />
            </AssetTableCellLabel>
            <AssetTableCellValue>
              <Ellipsis text={getAssetFileName(asset)} />
            </AssetTableCellValue>
          </AssetTableRow>
          <AssetTableRow>
            <AssetTableCellLabel>
              <Ellipsis text='Folder' />
            </AssetTableCellLabel>
            <AssetTableCellValue>
              {permissions.canOnlySearch ? (
                <Ellipsis text={getAssetFolderDirectory(asset)} />
              ) : (
                <>
                  {asset.folderId ? (
                    <Link href={getPath(`assets?folderId=${asset.folderId}`)}>
                      <Ellipsis text={`Home/${getAssetFolderDirectory(asset)}/`} />
                    </Link>
                  ) : (
                    <Link href={getPath(`assets`)}>
                      <BodyText>Home</BodyText>
                    </Link>
                  )}
                </>
              )}
            </AssetTableCellValue>
          </AssetTableRow>
          <AssetTableRow>
            <AssetTableCellLabel>
              <Ellipsis text='Created' />
            </AssetTableCellLabel>
            <AssetTableCellValue>
              <Ellipsis text={new Date(asset.createdAt).toLocaleString()} />
            </AssetTableCellValue>
          </AssetTableRow>
          <AssetTableRow>
            <AssetTableCellLabel>
              <Ellipsis text='Created By' />
            </AssetTableCellLabel>
            <AssetTableCellValue>
              <Ellipsis text={asset.creator?.email ?? ''} />
            </AssetTableCellValue>
          </AssetTableRow>
          <AssetTableRow>
            <AssetTableCellLabel>
              <Ellipsis text='Last Update' />
            </AssetTableCellLabel>
            <AssetTableCellValue>
              <Ellipsis text={new Date(asset.updatedAt).toLocaleString()} />
            </AssetTableCellValue>
          </AssetTableRow>
          <AssetTableRow>
            <AssetTableCellLabel sx={{ verticalAlign: 'top' }}>
              <Ellipsis text='Status' />
            </AssetTableCellLabel>
            <AssetTableCellValue sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              <Typography variant='body2'>{getAssetStateDisplayText(asset.state)}</Typography>
            </AssetTableCellValue>
          </AssetTableRow>
          {isSemanticDescriptionVisibilityEnabled && asset.semanticDescription && (
            <AssetTableRow>
              <AssetTableCellLabel sx={{ verticalAlign: 'top' }}>
                <Typography variant='body2'>Semantic Description</Typography>
                <AiBadge />
              </AssetTableCellLabel>
              <AssetTableCellValue sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
                  {asset.semanticDescription}
                </Typography>
              </AssetTableCellValue>
            </AssetTableRow>
          )}
          <AssetLabelRow asset={asset} />
        </TableBody>
      </AssetTable>
    </StyledBox>
  );
};
